/**
 * Parser markdown des recherches IA en items cochables.
 *
 * Stratégie :
 * - Si le markdown contient des `### Titre`, chaque section h3 = un item.
 *   Le titre = ligne après `### `. Le body = reste de la section.
 * - Sinon, si le markdown contient des `## Titre`, chaque h2 = un item.
 * - Sinon, pas d'items (le rendu reste un bloc unique non-cochable).
 *
 * La `key` d'un item est le titre normalisé (lowercase + trim) — identique
 * côté backend pour cohérence (cf. backend/app/ai_research/parser.py).
 */

export type ResearchItem = {
  key: string;
  title: string;
  body: string;
  /** Niveau de heading utilisé pour le parsing (2 ou 3). */
  level: 2 | 3;
};

export function normalizeItemKey(title: string): string {
  return title.trim().toLowerCase().replace(/\s+/g, ' ');
}

export function parseResearchItems(markdown: string): {
  preamble: string;
  items: ResearchItem[];
} {
  if (!markdown || !markdown.trim()) return { preamble: '', items: [] };

  // Cherche d'abord les ### (granularité fine), fallback sur ##.
  const h3Regex = /^### +(.+)$/gm;
  const h3Matches: Array<{ idx: number; title: string }> = [];
  let m: RegExpExecArray | null;
  while ((m = h3Regex.exec(markdown)) !== null) {
    h3Matches.push({ idx: m.index, title: m[1]!.trim() });
  }

  const useLevel: 2 | 3 = h3Matches.length >= 2 ? 3 : 2;

  if (useLevel === 3 && h3Matches.length >= 2) {
    const preamble = markdown.slice(0, h3Matches[0]!.idx).trim();
    const items: ResearchItem[] = h3Matches.map((curr, i) => {
      const next = h3Matches[i + 1];
      const startBody = curr.idx + `### ${curr.title}`.length;
      const endBody = next ? next.idx : markdown.length;
      const body = markdown.slice(startBody, endBody).trim();
      return {
        key: normalizeItemKey(curr.title),
        title: curr.title,
        body,
        level: 3,
      };
    });
    return { preamble, items };
  }

  // Sinon, fallback h2.
  const h2Regex = /^## +(.+)$/gm;
  const h2Matches: Array<{ idx: number; title: string }> = [];
  while ((m = h2Regex.exec(markdown)) !== null) {
    h2Matches.push({ idx: m.index, title: m[1]!.trim() });
  }

  if (h2Matches.length >= 2) {
    const preamble = markdown.slice(0, h2Matches[0]!.idx).trim();
    const items: ResearchItem[] = h2Matches.map((curr, i) => {
      const next = h2Matches[i + 1];
      const startBody = curr.idx + `## ${curr.title}`.length;
      const endBody = next ? next.idx : markdown.length;
      const body = markdown.slice(startBody, endBody).trim();
      return {
        key: normalizeItemKey(curr.title),
        title: curr.title,
        body,
        level: 2,
      };
    });
    return { preamble, items };
  }

  return { preamble: markdown, items: [] };
}
